import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { motion } from 'framer-motion';
import { Row, Col } from 'react-bootstrap';
import { SparklesIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import AdCard from './Ads/AdCard';
import { GET_NEW_ADS } from '../graphql/queries';

interface InfiniteScrollSectionProps {
  title?: string;
  limit?: number;
}

const InfiniteScrollSection: React.FC<InfiniteScrollSectionProps> = ({
  title = 'Fresh Recommendations',
  limit = 10,
}) => {
  const [ads, setAds] = useState<any[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const observerTarget = useRef<HTMLDivElement>(null);

  const { fetchMore } = useQuery(GET_NEW_ADS, {
    variables: { limit, offset: 0 },
    onCompleted: (data) => {
      if (data?.newAds) {
        setAds(data.newAds);
        setHasMore(data.newAds.length === limit);
      }
      setInitialLoading(false);
    },
    onError: () => {
      setInitialLoading(false);
    },
  });

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const result = await fetchMore({
        variables: { limit, offset: offset + limit },
      });

      if (result.data?.newAds) {
        const newAds = result.data.newAds;
        setAds((prev) => [...prev, ...newAds]);
        setOffset((prev) => prev + limit);
        setHasMore(newAds.length === limit);
      }
    } catch (error) {
      console.error('Error loading more ads:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, offset, limit, fetchMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, loadMore]);

  if (ads.length === 0 && !loading && !initialLoading) {
    return null;
  }

  return (
    <section className="py-4">
      {/* Section Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-3">
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: 44,
              height: 44,
              borderRadius: 14,
              background: 'rgba(74, 132, 217, 0.1)',
              color: 'var(--pw-info)',
            }}
          >
            <SparklesIcon style={{ width: 24, height: 24 }} />
          </div>
          <h2 className="fs-3 fw-bold mb-0" style={{ color: 'var(--pw-text)' }}>
            {title}
          </h2>
        </div>
        <span style={{ fontSize: 14, color: 'var(--pw-text-muted)', fontWeight: 500 }}>
          {ads.length} items
        </span>
      </div>

      {/* Initial Loading Skeleton */}
      {initialLoading && (
        <Row className="g-3 g-md-4">
          {[1, 2, 3, 4].map((i) => (
            <Col key={i} xs={6} md={4} lg={4} xl={3}>
              <div
                style={{
                  background: 'var(--pw-surface)',
                  border: '1px solid var(--pw-border)',
                  borderRadius: 0,
                  overflow: 'hidden',
                }}
              >
                <div className="skeleton" style={{ aspectRatio: '4/3' }} />
                <div className="p-3">
                  <div className="skeleton mb-2" style={{ height: 18, width: '60%', borderRadius: 6 }} />
                  <div className="skeleton mb-2" style={{ height: 14, width: '80%', borderRadius: 6 }} />
                  <div className="skeleton" style={{ height: 12, width: '40%', borderRadius: 4 }} />
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}

      {/* Ads Grid */}
      {!initialLoading && (
        <Row className="g-3 g-md-4">
          {ads.map((ad: any, index: number) => (
            <Col key={`${ad.id}-${index}`} xs={6} md={4} lg={4} xl={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.04, 0.4), duration: 0.4 }}
              >
                <AdCard ad={ad} />
              </motion.div>
            </Col>
          ))}
        </Row>
      )}

      {/* Load More Button */}
      {!initialLoading && hasMore && !loading && (
        <div className="d-flex justify-content-center pt-5">
          <button
            onClick={loadMore}
            className="d-flex align-items-center gap-2 border-0 fw-semibold"
            style={{
              padding: '14px 28px',
              borderRadius: 14,
              background: 'var(--pw-surface)',
              borderWidth: '1.5px',
              borderStyle: 'solid',
              borderColor: 'var(--pw-border)',
              color: 'var(--pw-text)',
              fontSize: 15,
              boxShadow: '0 4px 14px rgba(116, 67, 35, 0.06)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--pw-bg-soft)';
              e.currentTarget.style.borderColor = '#f2b58f';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 22px rgba(116, 67, 35, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--pw-surface)';
              e.currentTarget.style.borderColor = 'var(--pw-border)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(116, 67, 35, 0.06)';
            }}
          >
            <ArrowDownIcon style={{ width: 18, height: 18 }} />
            Load More
          </button>
        </div>
      )}

      {/* Loading Spinner */}
      {!initialLoading && loading && (
        <div className="d-flex justify-content-center py-5">
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              border: '3px solid var(--pw-border)',
              borderTopColor: 'var(--pw-primary)',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* End of List Message */}
      {!initialLoading && !hasMore && ads.length > 0 && (
        <div className="text-center py-5">
          <p className="mb-0" style={{ fontSize: 15, color: 'var(--pw-text-muted)' }}>
            You&apos;ve seen all the ads
          </p>
        </div>
      )}

      {/* Observer Target for automatic infinite scroll */}
      {!initialLoading && hasMore && (
        <div ref={observerTarget} style={{ height: 40 }} />
      )}
    </section>
  );
};

export default InfiniteScrollSection;
